/* eslint-disable no-nested-ternary */
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState, useRef } from "react";
import "react-circular-progressbar/dist/styles.css";
import PropTypes from "prop-types";
import { Typography, Stack } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import TargetPercentage from "components/TargetPercentage";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import Trend from "pages/Performance/components/Trend";
import UpdateKpiModal from "pages/Performance/components/UpdateKpiModal";
import { homeAction } from "redux/slice/home";
import { enqueueSnackbar } from "notistack";
import { numberFormatter } from "utils/HelperFunctions";
import ResizeObserver from "resize-observer-polyfill";
import Loader from "utils/Loader";
import TooltipLight from "utils/TooltipLight";

const initialWeekDaysValue = [
  { day: "sunday", key: "S", display: 0, value: 0, date: null },
  { day: "monday", key: "M", display: 1, value: 0, date: null },
  { day: "tuesday", key: "T", display: 1, value: 0, date: null },
  { day: "wednesday", key: "W", display: 1, value: 0, date: null },
  { day: "thursday", key: "T", display: 1, value: 0, date: null },
  { day: "friday", key: "F", display: 1, value: 0, date: null },
  { day: "saturday", key: "S", display: 0, value: 0, date: null },
];

function WeeklyTotals({
  selectedUser,
  ableToEditKpi = false,
  refetchData,
  selectedTab,
  view,
  handleCompetitionActivityUpdateOnSuccess,
  selectedTeam,
  selectedTeams,
  pointGoals = [],
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.loginUser.loginUserState);
  const { lastUpdated, outOfOfficeDayUpdated } = useSelector((state) => state.home.kpiEntries);
  const {
    data: weeklyTotals,
    loading: weeklyTotalsLoading,
    initialLoadComplete,
  } = useSelector((state) => state.home.weeklyTotals);

  const initialWeekDates = {
    startDate: moment.tz(undefined, user?.timezone).startOf("week").day(0),
    endDate: moment.tz(undefined, user?.timezone).endOf("week").day(6),
  };
  const [weekDates, setWeekDates] = useState(initialWeekDates);

  const [matchedUser, setMatchedUser] = useState(null);
  const [weekDays, setWeekDays] = useState(initialWeekDaysValue);
  const [ringSplit, setRingSplit] = useState(0);
  const metricRef = useRef(null);
  const [fontSize, setFontSize] = useState("1.8rem");
  const [trendType, setTrendType] = useState("percent");

  const [selectedDate, setSelectedDate] = useState();
  const [selectedDateOutOfOfficeDay, setSelectedDateOutOfOfficeDay] = useState(false);
  const [isUpdateKpiModalOpen, setIsUpdateKpiModalOpen] = useState(false);

  const handleDialogClose = (isSave) => {
    setIsUpdateKpiModalOpen(false);
    setSelectedDate();
    setSelectedDateOutOfOfficeDay(false);
    if (isSave && refetchData) {
      refetchData();
    }
  };

  const checkSkippedWeekDays = () => {
    const daysOfWeek = user.workingDays;
    const skipSunday = daysOfWeek?.length ? !Number(daysOfWeek[0]) : true;
    const skipSaturday = daysOfWeek?.length ? !Number(daysOfWeek.at(-1)) : true;

    return {
      skipSunday,
      skipSaturday,
    };
  };

  const fetchPerformanceData = () => {
    const { skipSunday, skipSaturday } = checkSkippedWeekDays();
    const payloadBody = {
      startDate: moment(weekDates.startDate).add(skipSunday ? 1 : 0),
      endDate: moment(weekDates.endDate).subtract(skipSaturday ? 1 : 0),
      timezone: user.timezone,
      filter: {
        teamList:
          selectedTab === 1
            ? selectedTeams?.length
              ? selectedTeams
              : []
            : user?.teamId
            ? [{ id: user.teamId }]
            : [],
        userList: selectedTab === 0 ? [{ id: user?.id }] : [],
        daysOfWeek: user.workingDays,
      },
    };
    dispatch(homeAction.getWeeklyTotalsRequest(payloadBody));
  };

  const computeRingSplit = () => {
    const displayLength = weekDays.filter((day) => day.display === 1).length;
    setRingSplit(Math.floor(displayLength / 2) + (displayLength % 2));
  };

  useEffect(() => {
    fetchPerformanceData();
  }, [weekDates, selectedTab, lastUpdated, selectedTeams, outOfOfficeDayUpdated]);

  useEffect(() => {
    refetchData?.();
  }, [outOfOfficeDayUpdated]);

  useEffect(() => {
    computeRingSplit();
  }, [weekDays]);

  useEffect(() => {
    const daysOfWeek = user?.workingDays?.split("");

    if (selectedUser) {
      const matchedUserFromWeeklyTotals = weeklyTotals?.find(
        (convertedPerformance) => convertedPerformance?.userId === selectedUser
      );

      const newWeekDays = (daysOfWeek || initialWeekDaysValue)?.map((dayOfWeek, index) => {
        const dateMatchingDay = moment(weekDates?.startDate).add(index, "days");
        const matchedTotalPerformance = matchedUserFromWeeklyTotals?.totalPerformance?.find(
          (eachPerformance) =>
            moment.tz(eachPerformance?.date, user?.timezone).isSame(dateMatchingDay, "day")
        );
        return {
          day: initialWeekDaysValue?.[index]?.day,
          key: initialWeekDaysValue?.[index]?.key,
          display: Number(dayOfWeek) || dayOfWeek?.display,
          value: matchedTotalPerformance?.points || 0,
          dailyTarget: matchedUserFromWeeklyTotals?.dailyTarget,
          date: dateMatchingDay,
          outOfOffice: matchedTotalPerformance?.outOfOffice || false,
        };
      });
      setWeekDays(newWeekDays);

      setMatchedUser(matchedUserFromWeeklyTotals);
    } else {
      // Only count members into averages when they have entered KPIs
      const membersWithCount = weeklyTotals?.filter((weeklyTotal) => weeklyTotal.totalCount > 0);

      const matchedUserFromWeeklyTotals = weeklyTotals?.reduce(
        (sum, individualUser) => {
          if (selectedTeam && selectedTeam.teamId !== individualUser.teamId) return sum;
          return {
            totalPoint:
              (parseInt(sum?.totalPoint, 10) || 0) +
              (parseInt(individualUser?.totalPoint, 10) || 0),
            totalCount:
              (parseInt(sum?.totalCount, 10) || 0) +
              (parseInt(individualUser?.totalCount, 10) || 0),
            // eslint-disable-next-line no-unsafe-optional-chaining
            averagePoints: (+sum?.averagePoints || 0) + (+individualUser?.averagePoints || 0),
            averagePointTrend:
              (parseFloat(sum?.averagePointTrend, 0) || 0) +
              (parseFloat(individualUser?.averagePointTrend, 0) || 0),
            trendTotalPoints:
              (parseFloat(sum?.trendTotalPoints, 0) || 0) +
              (parseFloat(individualUser?.trendTotalPoints, 0) || 0),
          };
        },
        {
          totalPoint: 0,
          totalCount: 0,
          averagePoints: 0,
          averagePointTrend: 0,
          trendTotalPoints: 0,
        }
      );

      matchedUserFromWeeklyTotals.id = "all";

      matchedUserFromWeeklyTotals.averagePoints = membersWithCount?.length
        ? (matchedUserFromWeeklyTotals.averagePoints / membersWithCount.length).toFixed(1)
        : 0;

      matchedUserFromWeeklyTotals.averagePointTrend = membersWithCount?.length
        ? (matchedUserFromWeeklyTotals.averagePointTrend / membersWithCount.length).toFixed(1)
        : 0;

      matchedUserFromWeeklyTotals.trendTotalPoints = membersWithCount?.length
        ? (matchedUserFromWeeklyTotals.trendTotalPoints / membersWithCount.length).toFixed(1)
        : 0;

      // Calculate total goal days
      const totalGoalDays = weeklyTotals?.reduce((acc, userObject) => {
        if (selectedTeam && selectedTeam.teamId !== userObject.teamId) return acc;
        acc += userObject.goalAchievedDays;
        return acc;
      }, 0);

      matchedUserFromWeeklyTotals.goalAchievedDays = totalGoalDays;

      setMatchedUser(matchedUserFromWeeklyTotals);

      const newWeekDays = (daysOfWeek || initialWeekDaysValue)?.map((dayOfWeek, index) => {
        const weekStartDay = moment(weekDates?.startDate).add(index, "day");

        let outOfOffice = true;

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < weeklyTotals.length; i++) {
          if (!outOfOffice) break;

          if (weeklyTotals[i]?.totalPerformance?.length) {
            if (!outOfOffice) break;

            outOfOffice =
              weeklyTotals[i].totalPerformance?.find((eachPerformance) =>
                moment.tz(eachPerformance?.date, user.timezone).isSame(weekStartDay, "day")
              )?.outOfOffice || false;
          }
        }

        return {
          day: initialWeekDaysValue?.[index]?.day,
          key: initialWeekDaysValue?.[index]?.key,
          display: Number(dayOfWeek) || dayOfWeek?.display,
          value: weeklyTotals?.reduce((accumulator, reportingUser) => {
            if (selectedTeam && selectedTeam.teamId !== reportingUser.teamId) return accumulator;
            return (
              accumulator +
              (reportingUser?.totalPerformance?.find((eachPerformance) =>
                moment.tz(eachPerformance?.date, user.timezone).isSame(weekStartDay, "day")
              )?.points || 0)
            );
          }, 0),
          dailyTarget: weeklyTotals?.reduce((accumulator, reportingUser) => {
            if (selectedTeam && selectedTeam.teamId !== reportingUser.teamId) return accumulator;
            return (
              accumulator +
              ((reportingUser?.inputKPIPermission &&
                reportingUser?.totalPerformance?.find((eachPerformance) =>
                  moment.tz(eachPerformance?.date, user.timezone).isSame(weekStartDay, "day")
                )?.dailyTarget) ||
                0)
            );
          }, 0),
          outOfOffice,
        };
      });

      setWeekDays(newWeekDays);
    }
  }, [weeklyTotals, selectedUser, selectedTeam]);

  useEffect(() => {
    const { skipSunday, skipSaturday } = checkSkippedWeekDays();

    const newStartDate = moment.tz(weekDates?.startDate, user?.timezone).startOf("week");
    const newEndDate = moment.tz(weekDates?.endDate, user?.timezone).endOf("week");

    if (
      (skipSunday || skipSaturday) &&
      (!moment(weekDates.startDate).isSame(newStartDate) ||
        !moment(weekDates.endDate).isSame(newEndDate))
    )
      setWeekDates({ startDate: newStartDate, endDate: newEndDate });
  }, [user.workingDays]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const { width } = entry.contentRect;

        window.requestAnimationFrame(() => {
          const fontSz = ((width - 90) / (130 - 90)) * (2 - 1.2) + 1.2;
          const clampedFontSize = Math.min(Math.max(fontSz, 1.2), 2);
          setFontSize(`${clampedFontSize}rem`);
          setTrendType(width < 100 ? "icon" : "percent");
        });
      });
    });

    if (metricRef.current) {
      resizeObserver.observe(metricRef.current);
    }

    return () => {
      if (metricRef.current) {
        resizeObserver.unobserve(metricRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const handleWeekDates = (type) => {
    const startWeekDay = 0;
    const endWeekDay = 6;

    const currentStartDate = moment.tz(undefined, user?.timezone).startOf("week").day(startWeekDay);
    const currentEndDate = moment.tz(undefined, user?.timezone).endOf("week").day(endWeekDay);
    let startDate = currentStartDate;
    let endDate = currentEndDate;

    switch (type) {
      case "prev":
        startDate = moment
          .tz(weekDates?.startDate, user?.timezone)
          .subtract(1, "weeks")
          .startOf("week")
          .day(startWeekDay);
        endDate = moment
          .tz(weekDates?.endDate, user?.timezone)
          .subtract(1, "weeks")
          .endOf("week")
          .day(endWeekDay);
        break;
      case "next":
        startDate = moment
          .tz(weekDates?.endDate, user?.timezone)
          .add(1, "weeks")
          .startOf("week")
          .day(startWeekDay);
        endDate = moment
          .tz(weekDates?.endDate, user?.timezone)
          .add(1, "weeks")
          .endOf("week")
          .day(endWeekDay);

        if (startDate.isAfter(currentStartDate) || endDate.isAfter(currentEndDate)) {
          startDate = currentStartDate;
          endDate = currentEndDate;
        }
        break;
      default:
        break;
    }

    setWeekDates({
      startDate,
      endDate,
    });
  };

  const displayedPeriod = checkSkippedWeekDays();

  const renderWeekday = (weekDay) => {
    if (!weekDay?.display) return null;
    let targetPercentageNumberShown;
    switch (view) {
      case "user":
        targetPercentageNumberShown = weekDay?.value;
        break;
      case "team":
      default:
        targetPercentageNumberShown = weekDay?.dailyTarget
          ? (Number(weekDay?.value) / Number(weekDay?.dailyTarget)) * 100
          : weekDay?.value;
        break;
    }

    const style = {
      display: "inline",
    };
    if (ableToEditKpi) style.cursor = "pointer";

    let streaking = false;
    if (weekDay.date) {
      const pointGoal = pointGoals.find((goal) =>
        moment.tz(goal.achievement_date, user.timezone).isSame(weekDay.date, "day")
      );
      if (pointGoal) {
        streaking = pointGoal.streaking || false;
      }
    }

    return (
      <TooltipLight key={weekDay?.day} title={weekDay?.outOfOffice ? "Out of office" : ""}>
        <MDBox
          p={0.6}
          sx={style}
          onClick={() => {
            if (!ableToEditKpi) return;
            if (
              moment
                .tz(weekDay?.date, user?.timezone)
                .startOf("day")
                .isAfter(moment.tz(undefined, user?.timezone).startOf("day"))
            ) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: "Future KPI data cannot be changed",
                }),
                {
                  variant: "custom",
                }
              );
            } else {
              setSelectedDate(weekDay?.date);
              setSelectedDateOutOfOfficeDay(!!weekDay?.outOfOffice);
              setIsUpdateKpiModalOpen(true);
            }
          }}
          data-testid={`weekly-totals-weekday-${weekDay?.day}`}
        >
          <MDTypography
            variant="caption"
            display="block"
            textAlign="center"
            sx={{ color: weekDay?.outOfOffice ? "#dedede" : "#344767" }}
          >
            {weekDay?.key}
          </MDTypography>
          <TargetPercentage
            percentage={targetPercentageNumberShown}
            circularPercentage={
              weekDay?.dailyTarget
                ? (Number(weekDay?.value) / Number(weekDay?.dailyTarget)) * 100
                : weekDay?.value
            }
            width="60px"
            height="60px"
            percentageSuffix={view === "user" ? "" : "%"}
            subTitle={null}
            typographyVariant="h6"
            outOfOffice={!!weekDay?.outOfOffice}
            displayIcon={streaking}
            weeklyRing
          />
        </MDBox>
      </TooltipLight>
    );
  };

  const renderMetric = (
    label,
    value,
    trendPrevious,
    trendCurrent,
    testId = null,
    suffix = null
  ) => (
    <Grid
      ref={metricRef}
      item
      xs={4}
      sx={{
        p: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "90px",
      }}
    >
      <Stack textAlign="center" justifyContent="flex-end" height="100%">
        <MDTypography variant="caption">{label}</MDTypography>
        <Stack
          direction="row"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="center"
          data-testid={testId}
        >
          <MDTypography
            variant="h3"
            sx={{
              textAlign: "center",
              lineHeight: "2rem",
              fontSize,
            }}
          >
            {value}
          </MDTypography>
          {suffix !== null && (
            <MDTypography
              variant="h4"
              sx={{
                lineHeight: "2rem",
                fontSize: "0.9rem",
                pl: "0.1rem",
              }}
            >
              {suffix}
            </MDTypography>
          )}
          {trendPrevious !== null && trendCurrent !== null && (
            <MDBox
              sx={{
                display: "inline-flex",
                alignItems: "flex-end",
              }}
            >
              <Trend prev={trendPrevious} curr={trendCurrent} type={trendType} />
            </MDBox>
          )}
        </Stack>
      </Stack>
    </Grid>
  );

  const showLoader = !!(weeklyTotalsLoading && !initialLoadComplete);

  return (
    <>
      <Card sx={{ height: "320px" }}>
        <Loader loading={showLoader} />
        <MDBox
          p={2}
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            height: "100%",
            overflowWrap: "anywhere",
            "& .CircularProgressbar-text": { fontSize: "12px" },
          }}
        >
          <MDBox
            pl={1}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "& .big-text": { fontSize: "1rem", fontWeight: "bold" },
            }}
          >
            <Typography variant="h6" color="info" mb={0} display="block">
              Weekly Totals
            </Typography>
            <MDBox
              sx={{
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                mx: "-10px",
              }}
            >
              <ArrowLeft
                style={{ cursor: "pointer" }}
                onClick={() => handleWeekDates("prev")}
                sx={{
                  mr: "-3px",
                }}
              />
              <Typography variant="caption" color="info" mb={0} display="block">
                {moment(weekDates.startDate)
                  .add(displayedPeriod.skipSunday ? 1 : 0, "days")
                  .format("MMM D")}{" "}
                -{" "}
                {moment(weekDates.endDate)
                  .subtract(displayedPeriod.skipSaturday ? 1 : 0, "days")
                  .format("MMM D")}
              </Typography>
              <ArrowRight
                style={{ cursor: "pointer" }}
                onClick={() => handleWeekDates("next")}
                sx={{
                  ml: "-3px",
                }}
              />
            </MDBox>
          </MDBox>

          <Stack
            sx={{ flexGrow: 1, justifyContent: "center", alignItems: "center", width: "100%" }}
          >
            <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
              <Grid container alignItems="center" justifyContent="center" sx={{ flexWrap: "wrap" }}>
                <MDBox sx={{ display: "inline-flex", flexWrap: "nowrap" }}>
                  {weekDays
                    ?.filter((x) => x.display)
                    .slice(0, ringSplit)
                    .map((weekDay) => renderWeekday(weekDay))}
                </MDBox>
                <MDBox sx={{ display: "inline-flex", flexWrap: "nowrap" }}>
                  {weekDays
                    ?.filter((x) => x.display)
                    .slice(ringSplit)
                    .map((weekDay) => renderWeekday(weekDay))}
                </MDBox>
              </Grid>
            </Grid>

            <Grid
              container
              alignItems="center"
              justifyContent="center"
              sx={{
                "& .subtext": { fontSize: "12px", paddingBottom: ".6rem" },
              }}
            >
              {renderMetric(
                "Average Points",
                matchedUser?.averagePoints
                  ? numberFormatter(matchedUser.averagePoints, true)
                  : "0.0",
                matchedUser?.averagePointTrend,
                matchedUser?.averagePoints,
                "weekly-totals-average-points"
              )}
              {renderMetric(
                "Total Points",
                matchedUser?.totalPoint || 0,
                matchedUser?.trendTotalPoints,
                matchedUser?.totalPoint,
                "weekly-totals-total-points"
              )}
              {renderMetric(
                "10 Point Days (all time)",
                matchedUser?.goalAchievedDays || 0,
                null,
                null,
                null
              )}
            </Grid>
          </Stack>
        </MDBox>
      </Card>

      {ableToEditKpi ? (
        <UpdateKpiModal
          selectedDate={selectedDate}
          open={isUpdateKpiModalOpen}
          setOpen={setIsUpdateKpiModalOpen}
          handleClose={handleDialogClose}
          handleCompetitionActivityUpdateOnSuccess={handleCompetitionActivityUpdateOnSuccess}
          isOutOfOfficeDay={selectedDateOutOfOfficeDay}
        />
      ) : null}
    </>
  );
}

export default WeeklyTotals;

WeeklyTotals.defaultProps = {
  ableToEditKpi: false,
  refetchData: null,
};

WeeklyTotals.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedUser: PropTypes.number.isRequired,
  ableToEditKpi: PropTypes.bool,
  refetchData: PropTypes.func,
  selectedTab: PropTypes.number.isRequired,
};
